import { PayloadAction } from '@reduxjs/toolkit';
import type { PaginatedResponse } from '@dynamika-shared/types/general';

export type QueryUser = {
  id: string;
  username?: string;
  firstName: string;
  lastName: string;
  email: string;
};

export enum QueryEvent {
  SUBMIT = 'SUBMIT',
  RESPOND = 'RESPOND',
  NEEDS_REWORK = 'NEEDS_REWORK',
  CLOSE = 'CLOSE',
  REOPEN = 'REOPEN',
  SEND_MESSAGE = 'SEND_MESSAGE',
  ESCALATE = 'ESCALATE',
}

export enum QueryStatus {
  CREATED = 'CREATED',
  OPEN_SITE = 'OPEN_SITE',
  OPEN_IAG = 'OPEN_IAG',
  CLOSED = 'CLOSED',
}

export type QueryItem = {
  id: string;
  clinicalTrialId: string;
  siteId: string;
  siteCode: string;
  cohortId?: string;
  patientId: string;
  visitId: string;
  uploadId: string;
  queryNumber: string;
  types: string[];
  status: QueryStatus;
  authorId: string;
  cohortName?: string;
  visitName: string;
  patientCode: string;
  patientDob?: string;
  created: string;
  closed: string | null;
  turnaroundTime: string;
  description: string;
  recipientIds: string[];
  messageCount: number;
  attachmentCount: number;
};

export type QueryTicketMessageAttachmentDto = {
  id: string; // uuid
  filename: string;
  contentType: string;
  size: number; // int64
  downloadUrl: string;
};

export type QueryTicketMessageDto = {
  id: string;
  created: string;
  actedBy: string;
  event: QueryEvent | string;
  sourceStatus: QueryStatus | string;
  targetStatus: QueryStatus | string;
  message: string;
  attachments: QueryTicketMessageAttachmentDto[];
};

export type QueryTicketDto = {
  id: string;
  clinicalTrialId: string;
  siteId: string;
  siteCode: string;
  cohortId?: string;
  patientId: string;
  visitId: string;
  uploadId: string;
  queryNumber: string;
  author: QueryUser;
  types: string[];
  recipients: QueryUser[];
  messages: QueryTicketMessageDto[];
  status: QueryStatus;
  visitName: string;
  patientCode: string;
  created: string;
  closed: string | null;
  description: string;
  patientDob?: string;
  cohortName?: string;
};

export type UpdateQueryRequest = {
  event: QueryEvent;
  actedBy: string;
  message: string;
  attachments: File[];
  userCredentials?: {
    username: string;
    password: string;
    signAgreement: boolean;
  };
};

export type QueryTicketMessageRequest = {
  siteId: string;
  patientId: string;
  visitId: string;
  uploadId: string;
  author: string;
  types: string[];
  recipients: string[];
  message: {
    event: QueryEvent;
    actedBy: string;
    message: string;
    attachments: File[];
  };
};

export type QueryListResponse = PaginatedResponse<QueryItem>;

export type QueriesFilters = {
  status?: QueryStatus;
  search?: string;
  visitId?: string;
  sort?: string;
  page?: number;
  size?: number;
};

export type QueriesState = {
  /**
   * Clinical trial the cached list belongs to
   *
   * Plain terms
   * -----------
   * A query list always belongs to one study. Remembering which study it came
   * from is what lets the app throw the list away the moment the user opens
   * another study, instead of briefly showing one study's queries under
   * another study's name.
   *
   * Technical
   * ---------
   * Written by the `fetchQueries` / `resetQueries` reducers from the `:id`
   * route param. `ReadOnlyQueriesList` renders rows only while this equals the
   * route id, so a stale `data` can never reach the table.
   */
  clinicalTrialId: string | null;
  data: QueryListResponse | null;
  loading: boolean;
  error?: Error;
  filters: QueriesFilters;
  currentQuery: QueryTicketDto | null;
  currentQueryLoading: boolean;
  currentQueryError?: Error;
};

export type QueriesFetchAction = PayloadAction<string>;

export type QueriesFetchSuccessAction = PayloadAction<QueryListResponse>;

export type QueryFetchAction = PayloadAction<{
  queryId: string;
  clinicalTrialId: string;
}>;

export type QueryFetchSuccessAction = PayloadAction<QueryTicketDto>;

export type SetQueriesFilterAction = PayloadAction<{
  filterQuery: keyof QueriesFilters;
  filterValue: string;
}>;
